import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Play, Pause, Radio, Code2, Database, Music2, Eye, Send, Layers, ShoppingBag } from "lucide-react";
import { useSiteContent } from "../../context/SiteContext";
import { useAudio } from "../../context/AudioContext";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_API_URL || "https://effortless-metaphor-api.ogtommype.workers.dev";

const PAGES = [
  { to: "/producers-suite", code: "01", label: "Producer's Suite", desc: "Apps & tools", Icon: Code2 },
  { to: "/headphones", code: "02", label: "Headphones", desc: "Spotify player", Icon: Radio },
  { to: "/beatbox", code: "03", label: "Beatbox", desc: "Beats database", Icon: Database },
  { to: "/soundscape", code: "04", label: "Soundscape", desc: "Music catalog", Icon: Music2 },
  { to: "/unknown-signals", code: "05", label: "Unknown Signals", desc: "Transmissions", Icon: Eye },
];

// "The Platform" / ecosystem cards are now managed in /admin (site_config "platform").
const platformStatusBadge = (status) => {
  const live = String(status || "").toLowerCase() === "live";
  return `label-mono text-xs px-2 py-0.5 border ${live ? "text-neon border-neon/40 bg-neon/5" : "text-neutral-500 border-[#27272A]"}`;
};

function PlatformFeatured({ app }) {
  return (
    <a href={app.url || "#"} target="_blank" rel="noreferrer" className="em-card p-6 group block relative overflow-hidden h-full">
      <div className="absolute top-0 left-0 right-0 h-px bg-neon opacity-60" />
      <div className="flex items-start justify-between mb-4">
        <span className={platformStatusBadge(app.status)}>{String(app.status || "live").toUpperCase()}</span>
        <ArrowUpRight size={16} className="text-neutral-600 group-hover:text-neon group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
      </div>
      <div className="font-display font-black text-2xl lg:text-3xl group-hover:text-neon transition-colors">{app.name}</div>
      {app.tagline && <div className="label-mono text-neutral-500 mt-1">{app.tagline}</div>}
      {app.blurb && <p className="text-neutral-400 text-sm mt-4 leading-relaxed">{app.blurb}</p>}
      {Array.isArray(app.stats) && app.stats.length > 0 && (
        <div className="flex items-center gap-6 mt-6 pt-4 border-t border-[#27272A]">
          {app.stats.map((s, i) => (
            <div key={i}>
              <div className="font-mono text-neon text-sm">{s.value}</div>
              <div className="label-mono text-neutral-600 text-xs mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      )}
    </a>
  );
}

function PlatformCard({ app }) {
  return (
    <a href={app.url || "#"} target="_blank" rel="noreferrer" className="em-card p-5 group block">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-display font-bold text-lg group-hover:text-neon transition-colors">{app.name}</div>
          {app.tagline && <div className="label-mono text-neutral-500 text-xs mt-0.5">{app.tagline}</div>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={platformStatusBadge(app.status)}>{String(app.status || "live").toUpperCase()}</span>
          <ArrowUpRight size={14} className="text-neutral-600 group-hover:text-neon group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
        </div>
      </div>
      {app.blurb && <p className="text-neutral-500 text-xs mt-3 leading-relaxed">{app.blurb}</p>}
    </a>
  );
}

export default function Landing() {
  const content = useSiteContent();
  const audio = useAudio();
  const { label, artist, latestRelease } = content;
  const platformApps = content.platform || [];
  const featuredApps = platformApps.filter((a) => a.featured);
  const restApps = platformApps.filter((a) => !a.featured);
  const [contactForm, setContactForm] = useState({ handle: "", message: "" });
  const [sending, setSending] = useState(false);

  const submitContact = async (e) => {
    e.preventDefault();
    if (!contactForm.message.trim()) return toast.error("Message is empty");
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(contactForm),
      });
      if (res.ok) {
        toast.success("Signal received.");
        setContactForm({ handle: "", message: "" });
      } else {
        const data = await res.json();
        toast.error(data.error || "Failed to send");
      }
    } catch {
      toast.error("Transmission failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="page-landing">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#27272A]">
        <div
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage:
              "linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0A0A0A 100%), radial-gradient(circle at 20% 50%, rgba(0, 255, 106, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(0, 255, 106, 0.05) 0%, transparent 50%)",
            backgroundSize: "100% 100%, 100% 100%, 100% 100%",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/40 via-[#0A0A0A]/70 to-[#0A0A0A]" />
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="scanline-bar" />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-24 pb-32 lg:pt-32 lg:pb-40">
          <div className="label-mono text-neon mb-6 flex items-center gap-3">
            <span className="w-2 h-2 bg-neon animate-pulse" />
            TRANSMISSION ONLINE // {label.city}
          </div>

          <h1 className="font-display font-black leading-[0.95] tracking-tighter text-5xl sm:text-7xl lg:text-[9rem]">
            EFFORTLESS
            <br />
            <span className="neon-text">METAPHOR</span>
          </h1>

          <p className="font-mono text-neutral-300 mt-6 text-sm sm:text-base">
            // an independent rap label · operated by{" "}
            <span className="text-neon">{artist.name}</span>
          </p>

          <p className="max-w-2xl text-neutral-400 mt-8 text-base sm:text-lg leading-relaxed">
            {label.shortDescription}
          </p>

          <div className="flex flex-wrap gap-4 mt-10">
            <a href="#latest-drop" data-testid="hero-play-btn" className="btn-primary inline-flex items-center gap-2">
              <Play size={14} /> PLAY LATEST
            </a>
            <Link to="/soundscape" data-testid="hero-catalog-btn" className="btn-secondary inline-flex items-center gap-2">
              CATALOG <ArrowUpRight size={14} />
            </Link>
            <Link to="/store" data-testid="hero-store-btn" className="btn-secondary inline-flex items-center gap-2">
              STORE <ShoppingBag size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* BENTO NAV GRID */}
      <section className="border-b border-[#27272A]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="label-mono text-neutral-500">// nodes</div>
              <h2 className="font-display font-bold text-3xl lg:text-5xl mt-2">
                Enter the system
              </h2>
            </div>
            <p className="text-neutral-400 max-w-md text-sm">
              Five subsystems. One artist. Click anything to load its terminal.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {PAGES.map(({ to, code, label: l, desc, Icon }) => (
              <Link
                key={to}
                to={to}
                data-testid={`bento-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="em-card p-6 group block min-h-[200px] flex flex-col justify-between"
              >
                <div className="flex items-start justify-between">
                  <span className="label-mono text-neutral-500">{code}</span>
                  <Icon
                    size={20}
                    className="text-neutral-500 group-hover:text-neon transition-colors"
                  />
                </div>
                <div>
                  <div className="font-display font-bold text-lg group-hover:text-neon transition-colors">
                    {l}
                  </div>
                  <div className="text-neutral-500 text-sm mt-1">{desc}</div>
                </div>
                <ArrowUpRight
                  size={18}
                  className="text-neutral-600 group-hover:text-neon group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
                />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* LATEST DROP */}
      <section id="latest-drop" className="border-b border-[#27272A]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="relative em-card aspect-square overflow-hidden bg-gradient-to-br from-[#111111] to-[#0A0A0A]">
            {latestRelease.coverUrl ? (
              <img
                src={latestRelease.coverUrl}
                alt={latestRelease.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#00FF6A]/20 to-transparent blur-2xl" />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="font-display font-black text-6xl text-neon/30">
                      {latestRelease.title?.split(" ").map(w => w[0]).join("").slice(0, 2) || "—"}
                    </div>
                  </div>
                </div>
              </>
            )}
            <div className="absolute top-4 left-4 label-mono bg-[#0A0A0A]/80 px-2 py-1 text-neon border border-neon/40">
              LATEST DROP
            </div>
          </div>
          <div>
            <div className="label-mono text-neutral-500">// {latestRelease.date}</div>
            <h3 className="font-display font-black text-4xl lg:text-6xl mt-3 tracking-tight">
              {latestRelease.title}
            </h3>

            {latestRelease.description && (
              <p className="text-neutral-400 mt-4 text-base leading-relaxed max-w-lg">
                {latestRelease.description}
              </p>
            )}

            <div className="grid grid-cols-2 gap-4 mt-8 max-w-xs">
              <div className="border border-[#27272A] p-4">
                <div className="label-mono text-neutral-500">Length</div>
                <div className="font-mono text-2xl text-neon mt-1">{latestRelease.runtime}</div>
              </div>
              <div className="border border-[#27272A] p-4">
                <div className="label-mono text-neutral-500">Format</div>
                <div className="font-mono text-2xl text-neon mt-1">{latestRelease.type}</div>
              </div>
            </div>

            {latestRelease.audioUrl && (
              <div className="mt-6">
                <button
                  onClick={() => audio.src === latestRelease.audioUrl ? audio.toggle() : audio.play(latestRelease.audioUrl)}
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="latest-play-btn"
                >
                  {audio.src === latestRelease.audioUrl && audio.playing ? <><Pause size={14} /> PAUSE</> : <><Play size={14} /> PLAY</>}
                </button>
              </div>
            )}

            <div className="mt-6 flex gap-4">
              <Link to="/soundscape" data-testid="latest-catalog-btn" className="btn-secondary inline-flex items-center gap-2">
                DIG DEEPER <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ARTIST / ABOUT */}
      <section className="border-b border-[#27272A]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="label-mono text-neutral-500">// operator</div>
            <h3 className="font-display font-bold text-3xl lg:text-5xl mt-3 leading-tight">
              {artist.name} <span className="neon-text">/</span> one signal, one source.
            </h3>
            <p className="text-neutral-400 mt-6 text-base sm:text-lg max-w-2xl leading-relaxed">
              {artist.bio}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10">
              {artist.stats.map((s) => (
                <div key={s.label} className="border border-[#27272A] p-4">
                  <div className="font-mono text-3xl text-neon">{s.value}</div>
                  <div className="label-mono text-neutral-500 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative em-card overflow-hidden min-h-[420px] bg-gradient-to-br from-[#111111] to-[#0A0A0A]">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#00FF6A]/20 to-transparent blur-2xl" />
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="font-display font-black text-6xl text-neon/30">EM</div>
              </div>
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <div className="label-mono text-neon">// SUBJECT</div>
              <div className="font-display font-bold text-xl">{artist.name}</div>
              <div className="text-neutral-400 text-sm">{artist.role}</div>
            </div>
          </div>
        </div>
      </section>

      {/* THE PLATFORM — EM ECOSYSTEM */}
      <section className="border-b border-[#27272A]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="label-mono text-neutral-500">// the platform</div>
              <h2 className="font-display font-bold text-3xl lg:text-5xl mt-2">
                Built different.
              </h2>
            </div>
            <p className="text-neutral-400 max-w-md text-sm">
              Effortless Metaphor isn't just a label — it's an operating system for music. Every tool below was built in-house to solve a real problem.
            </p>
          </div>

          {featuredApps.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid grid-cols-1 gap-4">
                {featuredApps.map((app) => (
                  <PlatformFeatured key={app.id || app.name} app={app} />
                ))}
              </div>
              <div className="grid grid-cols-1 gap-4">
                {restApps.map((app) => (
                  <PlatformCard key={app.id || app.name} app={app} />
                ))}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {restApps.map((app) => (
                <PlatformCard key={app.id || app.name} app={app} />
              ))}
            </div>
          )}

          <div className="mt-6 border border-[#27272A] p-4 flex items-center gap-3">
            <Layers size={14} className="text-neon flex-shrink-0" />
            <p className="text-neutral-500 text-xs font-mono">
              // One doctrine. Every tool built to the same spec: absorb the effort upstream so the artist never has to think about the system.
            </p>
          </div>
        </div>
      </section>

      {/* CONTACT — SEND A SIGNAL */}
      <section>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <div className="label-mono text-neutral-500">// transmit</div>
              <h3 className="font-display font-bold text-3xl lg:text-5xl mt-3">
                Send a signal
              </h3>
              <p className="text-neutral-400 mt-4 text-base leading-relaxed max-w-md">
                Direct line to OGTommyP. No inbox triage. No PR filter. Anonymous or signed — every message lands directly.
              </p>
              {label.contactEmail && (
                <div className="mt-6">
                  <div className="label-mono text-neutral-500">// direct</div>
                  <a href={`mailto:${label.contactEmail}`} className="font-mono text-neon hover:underline text-sm">
                    {label.contactEmail}
                  </a>
                </div>
              )}
            </div>
            <div className="em-card p-6">
              <form onSubmit={submitContact} className="flex flex-col gap-4" data-testid="contact-form">
                <div>
                  <label className="label-mono text-neutral-500">Handle (optional)</label>
                  <input
                    data-testid="contact-handle"
                    value={contactForm.handle}
                    onChange={(e) => setContactForm({ ...contactForm, handle: e.target.value })}
                    placeholder="anonymous"
                    maxLength={50}
                    className="mt-2 w-full bg-[#0A0A0A] border border-[#27272A] focus:border-neon outline-none px-3 py-2 font-mono text-sm"
                  />
                </div>
                <div>
                  <label className="label-mono text-neutral-500">Message</label>
                  <textarea
                    data-testid="contact-message"
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    rows={5}
                    maxLength={1000}
                    placeholder="> type your message..."
                    className="mt-2 w-full bg-[#0A0A0A] border border-[#27272A] focus:border-neon outline-none px-3 py-2 font-mono text-sm resize-none"
                  />
                </div>
                <button
                  data-testid="contact-submit"
                  disabled={sending}
                  className="btn-primary self-start inline-flex items-center gap-2 disabled:opacity-60"
                  type="submit"
                >
                  <Send size={14} />
                  {sending ? "SENDING..." : "SEND SIGNAL"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
